import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useCallback, useState } from "react";
// hooks
import { useLocalStorage } from "src/hooks/use-local-storage";
// utils
import { localStorageGetItem } from "src/utils/storage-available";
//
import { SettingsContext } from "./settings-context";
import { DEFAULT_SETTINGS } from "./default-settings";

// ----------------------------------------------------------------------
const THEME_MODE_KEY = "themeMode";

export function SettingsProvider({ children, defaultSettings = DEFAULT_SETTINGS }) {
  const [openDrawer, setOpenDrawer] = useState(false);

  const [settings, setSettings] = useLocalStorage("settings", defaultSettings);

  const [themeMode, setThemeMode] = useLocalStorage(
    THEME_MODE_KEY,
    settings?.themeMode ?? defaultSettings.themeMode
  );

  useEffect(() => {
    setThemeMode(themeMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isArabic = localStorageGetItem("i18nextLng") === "ar";

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang("ar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  const onUpdate = useCallback(
    (name, value) => {
      if (name === THEME_MODE_KEY) {
        setThemeMode(value);
        return;
      }

      setSettings(prevState => ({
        ...prevState,
        [name]: value,
      }));
    },
    [setSettings, setThemeMode]
  );

  // Direction by lang
  const onChangeDirectionByLang = useCallback(
    lang => {
      onUpdate("themeDirection", lang === "ar" ? "rtl" : "ltr");
    },
    [onUpdate]
  );

  // Reset
  const onReset = useCallback(() => {
    setSettings(defaultSettings);
    setThemeMode(defaultSettings.themeMode);
  }, [defaultSettings, setSettings, setThemeMode]);

  // Drawer
  const onToggleDrawer = useCallback(() => {
    setOpenDrawer(prev => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual({ ...settings, themeMode }, defaultSettings);

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      themeMode,
      onUpdate,
      // Direction
      onChangeDirectionByLang,
      // Reset
      canReset,
      onReset,
      // Drawer
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
    }),
    [
      onReset,
      onUpdate,
      settings,
      themeMode,
      canReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      onChangeDirectionByLang,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}

SettingsProvider.propTypes = {
  children: PropTypes.node,
  defaultSettings: PropTypes.object,
};
